<template>
  <div class="card-formulario">
    <div class="header p-3">
      <div class="d-flex align-items-center justify-content-between w-100">
        <div class="d-flex align-items-center gap-2">
          <div class="avatar-container">
            <IconOccupied
              v-if="formulario.checkIn?.nombre"
              :user="{ nombre: formulario.checkIn?.nombre }"
              class="avatar" />
            <IconFree v-else class="avatar" />
          </div>

          <div class="info-container">
            <h5 class="nombre mb-0">
              {{ formulario.checkIn?.nombre || $t('formulario.sinNombre') }} {{ formulario.checkIn?.apellidos || '' }}
            </h5>
          </div>
        </div>
        <button v-if="isExpanded" class="btn-chevron" @click="toggleExpand">
          <i class="fa-solid fa-chevron-up px-3"></i>
        </button>
      </div>
    </div>

    <div v-if="!isExpanded" class="estado-wrapper p-3">
      <div class="d-flex align-items-center gap-2">
        <template v-if="!isCompleted">
          <span class="estado-text me-1">{{ $t('reserva.checkIn.card.sinCompletar') }}</span>
          <button class="btn btn-primary d-flex align-items-center gap-2" @click="toggleExpand">
            <i class="fa-regular fa-pen-to-square icon-btn"></i>
            {{ $t('reserva.checkIn.card.completar') }}
          </button>
        </template>

        <template v-else>
          <span class="estado-text me-1">{{ $t('reserva.checkIn.card.guardado') }}</span>
          <button class="btn btn-secondary d-flex align-items-center gap-2" @click="toggleExpand">
            <img src="@/assets/icons/eye.svg" alt="ver" class="icon-eye" />
            {{ $t('reserva.checkIn.card.verRespuestas') }}
          </button>
        </template>
      </div>
    </div>

    <Transition name="collapse">
      <div v-show="isExpanded" class="content">
        <FormularioInscripcionReserva
          ref="formularioInscripcionReservaRef"
          :formulario="formulario"
          :instant="instant"
          @update-formulario="handleUpdateFormulario" />

        <div v-if="!instant" class="pb-4 d-flex justify-content-center">
          <MyGocuButton primary :label="$t('reserva.checkIn.card.enviar')" @click="enviarFormulario" />
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
  import { ref, onBeforeMount } from 'vue';
  import { createToaster } from '@meforma/vue-toaster';
  import { useI18n } from 'vue-i18n';
  import { realizarCheckIn } from '@/services/utils/FormularioInscripcionUtils';
  import IconOccupied from '@/components/Icons/IconOccupied.vue';
  import IconFree from '@/components/Icons/IconFree.vue';
  import FormularioInscripcionReserva from '@/modules/viajesGrupos/components/FormularioInscripcionReserva.vue';
  import MyGocuButton from '@/components/common/buttons/MyGocuButton.vue';
  import { useLoadingStore } from '@/store_pinia/loadingStore';
  import { informacionHabitacion } from '@/store_pinia/informacionHabitacion';

  const props = defineProps({
    formulario: {
      type: Object, //RReservaDatosFormularioInscripcionDTO
      required: true,
    },
    instant: {
      type: Boolean,
      default: false,
    },
  });

  const emit = defineEmits(['update-formulario', 'formulario-enviado']);

  const { t } = useI18n();
  const toaster = createToaster({ position: 'top-right' });
  const loadingStore = useLoadingStore();
  const storeHabitacion = informacionHabitacion();
  const viaje = storeHabitacion.getViaje;
  const currentFormData = ref(null);
  const isCompleted = ref(false);
  const isExpanded = ref(!isFormularioCompleted());
  const formularioInscripcionReservaRef = ref(null);

  function isFormularioCompleted() {
    if (props.instant) {
      if (
        viaje.formularioInscripcion?.preguntasEnabled &&
        viaje.formularioInscripcion?.preguntasInstant &&
        props.formulario.preguntasPendiente
      ) {
        return false;
      }
      if (
        viaje.formularioInscripcion?.checkInEnabled &&
        viaje.formularioInscripcion?.checkInInstant &&
        props.formulario.checkInPendiente
      ) {
        return false;
      }
      if (
        viaje.formularioInscripcion?.datosPersonalesEnabled &&
        viaje.formularioInscripcion?.datosPersonalesInstant &&
        props.formulario.datosPersonalesPendiente
      ) {
        return false;
      }
    } else {
      if (viaje.formularioInscripcion?.checkInEnabled && props.formulario.checkInPendiente) {
        return false;
      }
      if (viaje.formularioInscripcion?.datosPersonalesEnabled && props.formulario.datosPersonalesPendiente) {
        return false;
      }
      if (viaje.formularioInscripcion?.preguntasEnabled && props.formulario.preguntasPendiente) {
        return false;
      }
    }

    return true;
  }

  function toggleExpand() {
    isExpanded.value = !isExpanded.value;
  }

  function handleUpdateFormulario(data) {
    currentFormData.value = data;
    if (props.instant) {
      emit('update-formulario', data);
    }
  }

  function updateIsCompleted() {
    isCompleted.value = isFormularioCompleted();
  }

  function validate() {
    if (!formularioInscripcionReservaRef.value?.validateAndShowErrors?.()) {
      isExpanded.value = true;
      toaster.error(t('reserva.checkIn.error.formularioIncompleto'));
      return false;
    }
    if (!currentFormData.value) {
      toaster.error(t('reserva.checkIn.error.formularioIncompleto'));
      return false;
    }
    return true;
  }

  async function enviarFormulario() {
    try {
      loadingStore.show();
      if (!validate()) {
        return false;
      }

      const grupoId = props.formulario.grupoId;
      if (!grupoId) {
        console.error('Error: grupoId es undefined');
        toaster.error(t('reserva.checkIn.error.errorEnvio'));
        return false;
      }

      const formularioArray = [
        {
          ...props.formulario,
          ...currentFormData.value,
          grupoId,
        },
      ];

      const success = await realizarCheckIn(formularioArray, toaster, grupoId, props.instant);
      if (success) {
        isCompleted.value = true;
        currentFormData.value = {
          ...currentFormData.value,
          checkInPendiente: false,
          datosPersonalesPendiente: false,
          preguntasPendiente: false,
        };

        isExpanded.value = false;
        emit('formulario-enviado', {
          ...props.formulario,
          ...currentFormData.value,
        });
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error al enviar formulario:', error);
      toaster.error(t('reserva.checkIn.error.errorEnvio'));
      return false;
    } finally {
      loadingStore.hide();
    }
  }

  onBeforeMount(() => {
    updateIsCompleted();
  });

  defineExpose({
    enviarFormulario,
    isCompleted,
    validate,
  });
</script>

<style lang="scss" scoped>
  .card-formulario {
    background: var(--landing-background-color, white);
    transition: all 0.3s ease;

    border-radius: var(--bs-border-radius-xl);
    border: 1px solid rgba(var(--landing-text-color-rgb, var(--bs-primary-rgb)), 0.25);
    overflow: hidden;
    margin-bottom: 1.5rem;

    .header {
      height: 75px;
      background: var(--landing-primary-button-background-color, var(--bs-primary));
      padding: 1rem !important;

      .d-flex {
        width: 100%;
        min-width: 0;
      }

      .info-container {
        min-width: 0;
        flex: 1;
        margin-right: 1rem;
      }

      .nombre {
        color: var(--landing-primary-button-text-color, white);
        font-size: 16px;
        font-weight: 600;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        width: 100%;
        display: block;

        @media (max-width: 768px) {
          font-size: 13px;
        }
      }

      .avatar {
        width: 24px;
        height: 24px;
        font-size: 15px;
        flex-shrink: 0;
      }

      .btn-chevron {
        background: transparent;
        border: none;
        padding: 0;
        cursor: pointer;

        i {
          color: var(--landing-primary-button-text-color, white);
          font-size: 16px;
          transition: transform 0.3s ease;
        }
      }
    }

    .estado-wrapper {
      display: flex;
      justify-content: space-between;

      align-items: center;
      border-top: 1px solid rgba(var(--landing-text-color-rgb, var(--bs-primary-rgb)), 0.25);

      &.separator {
        border-bottom: 9px solid rgba(var(--landing-text-color-rgb, var(--bs-primary-rgb)), 0.25);
        margin-bottom: 1rem;
      }

      .estado-text {
        color: var(--landing-text-color, var(--bs-primary));
        font-size: 16px;
        font-weight: 400;

        @media (max-width: 768px) {
          font-size: 15px;
        }
      }
    }

    .content {
      border-top: 1px solid rgba(var(--landing-text-color-rgb, var(--bs-primary-rgb)), 0.25);
    }

    .btn-primary {
      height: 40px;
      padding: 0 16px;
      border-radius: calc(var(--landing-primary-button-border-radius, 20) * 1px);
      border: calc(var(--landing-primary-button-border, 0) * 1px) solid
        var(--landing-primary-button-text-color, var(--landing-text-color, var(--bs-primary)));
      background-color: var(--landing-primary-button-background-color, var(--bs-secondary)) !important;
      color: var(--landing-primary-button-text-color, var(--landing-text-color, var(--bs-primary))) !important;
      box-shadow: none !important;
      font-size: 13px;
      font-weight: var(--landing-primary-button-font-weight, 500);

      .icon-btn {
        color: inherit;
      }

      &:hover,
      &:focus,
      &:active {
        background-color: var(--landing-primary-button-text-color, var(--bs-primary)) !important;
        color: var(--landing-primary-button-background-color, var(--bs-secondary)) !important;
        border-color: var(--landing-primary-button-background-color, var(--bs-primary)) !important;
        box-shadow: none !important;
        opacity: 1;

        .icon-btn {
          color: inherit;
        }
      }
    }

    .btn-secondary {
      height: 40px;
      background-color: var(--landing-text-color, var(--blue-1)) !important;
      color: var(--landing-background-color, white) !important;
      border: none !important;
      border-radius: var(--bs-border-radius-xxl);
      box-shadow: none !important;
      font-size: 13px;
      font-weight: 500;

      &:hover {
        opacity: 0.85;
      }
    }
  }

  :deep(.icon-occupant-active) {
    background-color: var(--landing-primary-button-background-color, var(--bs-primary));
    border: 1px solid var(--landing-primary-button-text-color, var(--bs-secondary));
    color: var(--landing-primary-button-text-color, var(--bs-secondary));
  }

  .collapse-enter-active,
  .collapse-leave-active {
    transition: all 0.15s ease;
  }

  .collapse-enter-from,
  .collapse-leave-to {
    opacity: 0;
    transform: translateY(-10px);
  }

  .form-check-input:checked {
    background-color: var(--landing-detail-color, var(--bs-secondary));
    border-color: var(--landing-detail-color, var(--bs-secondary));
  }
</style>
